import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { queryClient } from '@/core/api/queryClient';

interface OrganizationState {
  activeOrganizationId: string | null;

  setActiveOrganization: (id: string) => void;
  clear: () => void;
}

export const useOrganizationStore = create<OrganizationState>()(
  persist(
    (set) => ({
      activeOrganizationId: null,

      setActiveOrganization: (id) => {
        set({ activeOrganizationId: id });
        // Org switch marks the entire query cache stale. Every org-scoped key
        // embeds orgId, so those queries refetch on their own once their key
        // changes (or on next mount); a filtered list would have to enumerate
        // every feature and has already drifted out of date twice.
        //
        // `refetchType: 'none'` is critical: a plain invalidate() force-refetches
        // every ACTIVE query immediately. When switching org away from a project
        // board, the board/project queries are still mounted for that tick and
        // would refetch with the NEW X-Tenant-Id but the OLD project/board id —
        // a burst of 404s for resources that don't exist in the target org.
        // Marking stale without refetching lets the leaving page unmount cleanly
        // and the destination page fetch fresh. See ADR "Multi-Tenancy with
        // activeOrganizationId".
        queryClient.invalidateQueries({ refetchType: 'none' });
      },

      clear: () => {
        set({ activeOrganizationId: null });
      },
    }),
    {
      name: 'organization-storage',
      partialize: (state) => ({
        activeOrganizationId: state.activeOrganizationId,
      }),
    },
  ),
);
